export const REGIONS = {
  EU: 'EU',
  UK: 'UK',
  US: 'US',
} as const;

export type Region = (typeof REGIONS)[keyof typeof REGIONS];

export interface RegionInfo {
  region: Region;
  countryCode: string;
}

export const DEFAULT_REGION: Region = REGIONS.UK;
export const DEFAULT_COUNTRY_CODE = 'GB';
export const REGION_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

/**
 * Shopify market handle → region, for resolving a visitor's IP country. Both spellings are accepted because the store's handles are
 * `europe` / `gb`, not the `eu` / `uk` this map originally assumed.
 */
export const MARKET_HANDLE_TO_REGION: Record<string, Region | undefined> = {
  europe: REGIONS.EU,
  eu: REGIONS.EU,
  gb: REGIONS.UK,
  uk: REGIONS.UK,
  america: REGIONS.US,
};

/**
 * EU member state ISO 3166-1 alpha-2 country codes (27 members).
 * Also includes EEA countries (Iceland, Liechtenstein, Norway) for GDPR coverage.
 */
export const EU_COUNTRY_CODES = new Set([
  'AT', // Austria
  'BE', // Belgium
  'BG', // Bulgaria
  'HR', // Croatia
  'CY', // Cyprus
  'CZ', // Czech Republic
  'DK', // Denmark
  'EE', // Estonia
  'FI', // Finland
  'FR', // France
  'DE', // Germany
  'GR', // Greece
  'HU', // Hungary
  'IE', // Ireland
  'IT', // Italy
  'LV', // Latvia
  'LT', // Lithuania
  'LU', // Luxembourg
  'MT', // Malta
  'NL', // Netherlands
  'PL', // Poland
  'PT', // Portugal
  'RO', // Romania
  'SK', // Slovakia
  'SI', // Slovenia
  'ES', // Spain
  'SE', // Sweden
  // EEA (covered by GDPR)
  'IS', // Iceland
  'LI', // Liechtenstein
  'NO', // Norway
]);

export const UK_COUNTRY_CODES = new Set([
  'GB', // United Kingdom
  'GG', // Guernsey
  'JE', // Jersey
  'IM', // Isle of Man
]);

// ponytail: US only today; add CA etc. here when the `america` market grows.
export const US_COUNTRY_CODES = new Set(['US']);

/**
 * Maps each region to its country code set.
 * Used by resolveRegionInfo() to dynamically match countries to regions.
 * Adding a new region here is all that's needed for the fallback to work.
 */
export const REGION_COUNTRY_CODES: Record<Region, Set<string>> = {
  [REGIONS.EU]: EU_COUNTRY_CODES,
  [REGIONS.UK]: UK_COUNTRY_CODES,
  [REGIONS.US]: US_COUNTRY_CODES,
};

/**
 * Representative country per region — the only country ever sent to Shopify
 * @inContext (see getLocaleFromRequest). The shopper's picked country is UI-only.
 */
export const REGION_DEFAULT_COUNTRY_CODES: Record<Region, string> = {
  [REGIONS.EU]: 'DE',
  [REGIONS.UK]: 'GB',
  [REGIONS.US]: 'US',
};

/**
 * Fallback currency per region, used only to satisfy the Locale type
 * when a country isn't in the countries map. Shopify returns actual
 * pricing currency via @inContext — this doesn't affect displayed prices.
 */
export const REGION_FALLBACK_CURRENCY: Record<Region, string> = {
  [REGIONS.EU]: 'EUR',
  [REGIONS.UK]: 'GBP',
  [REGIONS.US]: 'USD',
};

/**
 * The picker offers stores, not countries. Rest of World is the UK market under its
 * own label (GBP, GB pricing and checkout), so only the UI tells the two apart.
 * `countryCode` is what the region cookie stores and names the flag in
 * `public/assets/flags`; it is never sent to Shopify (see getLocaleFromRequest).
 */
export interface Store extends RegionInfo {
  label: string;
  name: string;
  details: string;
  /** Closes every product description (`ProductInfo`). */
  disclaimer: string;
}

export const STORES = {
  UK: {
    region: REGIONS.UK,
    countryCode: 'GB',
    label: 'UK',
    name: 'United Kingdom',
    details: '£ GBP · Ships from UK · VAT included',
    disclaimer:
      'Dispatched in 1 to 3 working days. Free UK delivery over £150.',
  },
  EU: {
    region: REGIONS.EU,
    countryCode: 'EU',
    label: 'EU',
    name: 'Europe',
    details: '€ EUR · Ships from Netherlands · Taxes & duties included',
    // ponytail: dated copy, goes stale after 24 Oct; edit here once the warehouse is live.
    disclaimer:
      'Dispatched from 24 October. Ships from our Netherlands warehouse — all taxes and duties included. Free delivery over €150.',
  },
  US: {
    region: REGIONS.US,
    countryCode: 'US',
    label: 'US',
    name: 'United States',
    details: '$ USD · Ships from UK · Duties paid on delivery',
    disclaimer:
      'Dispatched in 1 to 3 working days. Ships from the UK. Import taxes and duties are paid by the customer on delivery. Free US delivery over $200.',
  },
  ROW: {
    region: REGIONS.UK,
    countryCode: 'ROW',
    label: 'ROW',
    name: 'Rest of World',
    details: '£ GBP · Ships from UK · Duties paid on delivery',
    disclaimer:
      'Dispatched in 1 to 3 working days. Ships from the UK. Import taxes and duties are paid by the customer on delivery.',
  },
} as const satisfies Record<string, Store>;
